/**
 * App dark theme — unified navy/gold brand palette tuned for all-day application use.
 * Brand colors/fonts: @asi/design-tokens darkTheme (single source of truth).
 */

import { createTheme } from '@mui/material/styles';
import { darkTheme } from '@asi/design-tokens';

const {
  navyDeep: NAVY_DEEP,
  navy: NAVY,
  navyElevated: NAVY_ELEVATED,
  navySurface: NAVY_SURFACE,
  royalMid: ROYAL_MID,
  goldAntique: GOLD_ANTIQUE,
  goldPolished: GOLD_POLISHED,
  goldBrushed: GOLD_BRUSHED,
  goldMetalText,
  goldMetal,
  secondaryLight,
  secondaryDark,
  textPrimary,
  textSecondary,
  textDisabled,
  divider,
  error,
  warning,
  success,
  info,
} = darkTheme.colors;

export const APP_GOLD_METAL_TEXT = goldMetalText;

export const APP_GOLD_METAL = goldMetal;

/** Backward-compatible re-exports. */
export const DECISION_POINT_GOLD_METAL_TEXT = APP_GOLD_METAL_TEXT;
export const DECISION_POINT_GOLD_METAL = APP_GOLD_METAL;
export const VOXPONT_GOLD_METAL_TEXT = APP_GOLD_METAL_TEXT;
export const VOXPONT_GOLD_METAL = APP_GOLD_METAL;

export const FUSION_MSI_AUDIENCE_ACCENTS_DARK = {
  patient: { main: '#7BBFB5', soft: '#A8D8D2', dim: 'rgba(123,191,181,0.14)' },
  clinician: { main: '#B08FD4', soft: '#CDB8E8', dim: 'rgba(176,143,212,0.14)' },
  facility: { main: '#D4A85C', soft: '#E8CB94', dim: 'rgba(212,168,92,0.14)' },
  fde: { main: '#D97B55', soft: '#EDAC8A', dim: 'rgba(217,123,85,0.14)' },
};

/** SavoirCare board accents — dark-mode soft fills aligned to semantic palette. */
export const FUSION_MSI_BOARD_ACCENTS_DARK = {
  lounge: { bg: 'rgba(255, 181, 71, 0.14)', color: '#FFB547', border: '#FFB547' },
  location: {
    lounge: { bg: 'rgba(255, 181, 71, 0.14)', color: '#FFB547', border: '#FFB547' },
    trauma: { bg: 'rgba(255, 122, 122, 0.14)', color: '#FF7A7A', border: '#FF7A7A' },
    procedure: { bg: 'rgba(111, 177, 245, 0.14)', color: '#6FB1F5', border: '#6FB1F5' },
    default: { bg: 'rgba(91, 217, 138, 0.14)', color: '#5BD98A', border: '#5BD98A' },
  },
};

export const appDarkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: GOLD_ANTIQUE,
      light: GOLD_POLISHED,
      dark: GOLD_BRUSHED,
      contrastText: NAVY_DEEP,
    },
    secondary: {
      main: ROYAL_MID,
      light: secondaryLight,
      dark: secondaryDark,
      contrastText: '#FFFFFF',
    },
    background: {
      default: NAVY_DEEP,
      paper: NAVY_ELEVATED,
    },
    text: {
      primary: textPrimary,
      secondary: textSecondary,
      disabled: textDisabled,
    },
    divider,
    error: { main: error, contrastText: NAVY_DEEP },
    warning: { main: warning, contrastText: NAVY_DEEP },
    success: { main: success, contrastText: NAVY_DEEP },
    info: { main: info, contrastText: NAVY_DEEP },
    audience: FUSION_MSI_AUDIENCE_ACCENTS_DARK,
    board: FUSION_MSI_BOARD_ACCENTS_DARK,
  },
  typography: {
    fontFamily: darkTheme.fonts.body,
    h1: { fontFamily: darkTheme.fonts.display },
    h2: { fontFamily: darkTheme.fonts.display },
    h3: { fontFamily: darkTheme.fonts.display },
    h4: { fontFamily: darkTheme.fonts.display },
    h5: { fontFamily: darkTheme.fonts.display },
    h6: { fontFamily: darkTheme.fonts.display },
  },
  components: {
    MuiButton: {
      defaultProps: { variant: 'text' },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
    },
    MuiSelect: {
      defaultProps: { variant: 'outlined' },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: NAVY,
          color: textPrimary,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: NAVY,
          borderRight: '1px solid rgba(196, 154, 44, 0.2)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(196, 154, 44, 0.15)',
        },
        head: {
          backgroundColor: NAVY_SURFACE,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          borderColor: 'rgba(196, 154, 44, 0.22)',
        },
      },
    },
  },
});

export default appDarkTheme;

/** @deprecated Use appDarkTheme */
export const decisionPointDarkTheme = appDarkTheme;

/** @deprecated Use appDarkTheme */
export const voxPontDarkTheme = appDarkTheme;
